import { FiSearch } from "react-icons/fi";
import styles from "./Chats.module.css";
import { useEffect, useRef, useState } from "react";
import { IoCheckmarkDone } from "react-icons/io5";
import { IoIosArrowBack } from "react-icons/io";
import { VscSend } from "react-icons/vsc";
import { MdOutlineDownloadForOffline } from "react-icons/md";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  addDoc,
  writeBatch,
  doc,
  getFirestore,
  serverTimestamp,
} from "firebase/firestore";
import { initializeApp } from "@firebase/app";
import { useUserInfoSelector, useUserTokenSelector } from "store/reducers";
import moment from "moment";
import firebaseConfig from "Config/firebaseConfig";
import { fetchTherapistClients } from "actions/dataLoad";
import { useHistory, useParams } from "react-router-dom";
const firebaseApp = initializeApp(firebaseConfig);
const firestore = getFirestore(firebaseApp);

const Chats = () => {
  const [clientSelected, setClientSelected] = useState(null);
  const [messageList, setMessageList] = useState([]);
  const [message, setMessage] = useState("");
  const [chatsShown, setChatsShown] = useState(true);
  const [unreadMessageDisplayed, setUnreadMessageDisplayed] = useState(false);
  const [inputHeight, setInputHeight] = useState(30);
  const [file, setFile] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const userToken = useUserTokenSelector();
  const [selectedId, setSelectedId] = useState(null);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [refreshClients, setRefreshClients] = useState(false);
  const userInfo = useUserInfoSelector();
  const { clientId: clientIdFromUrl } = useParams();
  const history = useHistory();
  const textareaRef = useRef(null);

  const FilterOptions = [
    { id: 1, label: "New Clients" },
    { id: 2, label: "Active Clients" },
    { id: 3, label: "Inactive Clients" },
  ];
  const [clients, setClients] = useState([]);

  // Derive filteredData for the "All Contacts" view
  const chattedClientIds = clientsList.map(c => c.id);

  const unchattedClients = clients.filter(client => {
    const session = client.pastSession || client.upcomingSession;
    const userId = session?.userID;
  
    if (!userId) {
      return false;
    }
  
    return !chattedClientIds.includes(userId);
  });  

  const filteredData = selectedId === null
    ? unchattedClients // If "All" is selected under "All Contacts", show all unchatted clients
    : unchattedClients.filter((client) => { // Otherwise, filter unchatted clients by status
        const selectedFilterLabel = FilterOptions.find(
          (option) => option.id === selectedId
        )?.label;
        // Ensure client.status exists
        if (typeof client.status !== 'string') {
          return false;
        }
        if (selectedFilterLabel === "New Clients") {
          return client.status === "New Client";
        } else if (selectedFilterLabel === "Active Clients") {
          return client.status === "Active";
        } else if (selectedFilterLabel === "Inactive Clients") {
          return client.status === "Not Active";
        }
        return false;
        });

        const avatar = {
          female: "https://ucarecdn.com/c22b8f55-8c68-411e-a5c9-8b5b99c08701/femaleEmoji.svg",
          male: "https://ucarecdn.com/52c99efd-cba4-4fe1-93f2-e393088b7a0c/male.svg",
          other: "https://ucarecdn.com/c7bb4c01-2258-428d-b8ce-e2196ee63220/lgbtq.svg",
        };

  const date = new Date();
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();

  const convertTimestampToTime = (timestamp) => {
    // Create a Date object using the seconds from the Firebase timestamp
    const date = new Date(timestamp.seconds * 1000); // Convert seconds to milliseconds

    // Get hours and minutes
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Format the time to always display 2 digits (e.g., 14:30, not 14:5)
    const formattedTime = `${hours < 10 ? "0" + hours : hours}:${
      minutes < 10 ? "0" + minutes : minutes
    }`;

    return formattedTime;
  };

  useEffect(() => {
    const getClientsData = async () => {
      try {
        const res = await fetchTherapistClients({}, userToken);
        setClients(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching clients:", err);
      }
    };

    getClientsData();
  }, [userToken]);

  useEffect(() => {
    const markMessagesAsSeen = async () => {
      try {
        const chatsQuery = query(
          collection(firestore, "Chats"),
          where("receiverId", "==", userInfo.idTherapist),
          where("senderId", "==", clientSelected.id),
          where("status", "==", 1) // Unseen messages
        );

        const snapshot = await getDocs(chatsQuery);
        if (!snapshot.empty) {
          const batch = writeBatch(firestore);
          snapshot.forEach((document) => {
            const docRef = doc(firestore, "Chats", document.id);
            batch.update(docRef, {
              status: 3, // Mark as seen
              isNew: false, // Mark as not new
            });
          });
          await batch.commit();
        }
      } catch (error) {
        console.error("Error marking messages as seen:", error);
      }
    };

    if (clientSelected && clientSelected.id) {
      markMessagesAsSeen();
    }
  }, [clientSelected]);

  const getChatHistory = (clientId) => {
    try {
      const chatCollection = collection(firestore, "Chats");

      const chatQuery = query(
        chatCollection,
        // The Firestore query for the sender and receiver based on categoryId
        where("senderId", "in", [userInfo.idTherapist, clientId]),
        where("receiverId", "in", [clientId, userInfo.idTherapist]),
        orderBy("timestamp", "asc")
      );

      // Listening to changes in the "Chats" collection
      const unsubscribe = onSnapshot(chatQuery, (snapshot) => {
        const messageList = [];
        snapshot.docs.forEach((doc) => {
          messageList.push(doc.data());
        });

        setMessageList(messageList); // Update the state with the new messages
      });

      // Return the unsubscribe function for cleanup
      return unsubscribe;
    } catch (error) {
      console.error("Error getting chat history:", error);
    }
  };

  const getChatClients = () => {
    try {
      setLoading(true); // Show the loader or loading indicator

      // Create queries for messages from sender and receiver
      const receiverQuery = query(
        collection(firestore, "Chats"),
        where("receiverId", "==", userInfo.idTherapist),
        orderBy("timestamp", "asc")
      );

      const senderQuery = query(
        collection(firestore, "Chats"),
        where("senderId", "==", userInfo.idTherapist),
        orderBy("timestamp", "asc")
      );

      // Listen to changes in the receiver messages
      const unsubscribeReceiver = onSnapshot(
        receiverQuery,
        async (receiverSnapshot) => {
          const senderSnapshot = await getDocs(senderQuery);

          const response = [
            ...senderSnapshot.docs,
            ...receiverSnapshot.docs,
          ].sort(
            (a, b) => a.data().timestamp.seconds - b.data().timestamp.seconds
          ); // Sorting by timestamp

          const clientsList = response.map((item) => item.data());

          const groupedData = new Map();
          clientsList.forEach((item) => {
            const type =
              item.senderId === userInfo.idTherapist
                ? "receiver"
                : "sender";
            const clientId = item[`${type}Id`];

            if (groupedData.has(clientId)) {
              const existingClient = groupedData.get(clientId);
              existingClient.chats.push(item);

              const lastMessage =
                existingClient.chats[existingClient.chats.length - 1];
              if (
                !existingClient.clientIconUrl ||
                lastMessage.senderId !== userInfo.idTherapist
              ) {
                existingClient.clientIconUrl = item.receiverIconUrl;
              }
            } else {
              groupedData.set(clientId, {
                id: clientId,
                name: item[`${type}Name`],
                chats: [item],
                clientIconUrl: item.receiverIconUrl,
              });
            }
          });

          // Update client list with unread messages count and status
          const updatedClientsList = [...groupedData.values()]
              .map((client) => {
                const userMessages = client.chats.filter(
                  (chat) => chat.senderId !== userInfo.idTherapist
                );
                const unreadMessages = userMessages.filter((chat) => chat?.isNew).length;

                const lastMessage = client.chats[client.chats.length - 1];
                const status = lastMessage?.status;
                const lastTimestamp = lastMessage?.timestamp?.seconds || 0;

                return {
                  ...client,
                  count: unreadMessages,
                  type:
                    status === 1 ? "sent" : status === 2 ? "delivered" : "seen",
                  lastTimestamp, // Add this to sort later
                };
              })
              .sort((a, b) => b.lastTimestamp - a.lastTimestamp); // Most recent first


          setClientsList(updatedClientsList);
          setLoading(false); // Hide the loader
        }
      );

      // Cleanup on component unmount
      return () => unsubscribeReceiver();
    } catch (error) {
      console.error("Error getting the chats", error);
      setLoading(false); // Hide the loader in case of error
    }
  };

  useEffect(() => {
    const cleanupFn = getChatClients(); // Call the function on component mount
    return cleanupFn; 
  }, [refreshClients]);

  useEffect(() => {
    if (clientsList.length > 0 && clientIdFromUrl) {
      setClientSelected(clientsList.find(res => res.id === clientIdFromUrl));
    }
  }, [clientIdFromUrl, clientsList]);

  useEffect(() => {
    if (clientSelected) {
      const id = clientSelected?.id;
      setChatsShown(false);
      history.push("/dashboard/therapist/chats/"+ id);
      return getChatHistory(id);
    }
  }, [clientSelected]);

  const handleMessageSend = async () => {
    try {
      const messageData = {
        textContent: message,
        timestamp: serverTimestamp(),
        type: "text",
        senderId: userInfo.idTherapist,
        receiverId: clientSelected.id,
        senderName: userInfo.fullName,
        receiverName: clientSelected.name,
        receiverIconUrl: clientSelected.clientIconUrl || clientSelected.avatar_url || "",
        status: 1, // Start as unseen (1)
        isNew: true, // To track new messages
      };
      await addDoc(collection(firestore, "Chats"), messageData);
      setRefreshClients(prev => !prev);
      getChatHistory(clientSelected.id);
      // await sendChatNotification({title: `${userInfo.fullName} messaged you!`, body: message, receiverType: "user", receiverid: clientSelected.id}, userToken);
      setUnreadMessageDisplayed(false);
      setMessage("");
      setFile(null);
    } catch (error) {
      console.error("Error sending the message", error);
    }
  };
  const handleInputChange = (e) => {
    const textarea = textareaRef.current;
    setMessage(e.target.value);
  
    if (textarea) {
      textarea.style.height = "auto"; // Reset
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 30), 70);
      setInputHeight(newHeight);
    }
  };  
  const handleChats = () => {
    setChatsShown(true);
  };
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  const truncateText = (text, limit) => {
    return text.length > limit ? `${text.slice(0, limit)}.....` : text;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent newline
      handleMessageSend(); // Trigger send
    }
  };
  

  return (
    <div className={styles.chats}>
      <div
        className={`${styles["chats-left"]} ${chatsShown ? styles.active : ""}`}
      >
        {showAllContacts ? (
          <>
          <div className={styles["allChats-Header"]}>
            <div className={styles["allChats-backArrow-Bg"]} onClick={() => setShowAllContacts(false)}>
              <img src="https://ucarecdn.com/8c1645b5-ac4b-49d2-b2e2-f66eab1186da/leftVector.svg" alt="back"/>
            </div>
            <div className={styles["contact-filters-header"]}>
              <div
                className={`${styles["contact-filters-buttons"]} ${
                  selectedId === null ? styles.filterButtonSelected : ""
                }`}
                style={{ paddingLeft: "18px", paddingRight: "18px" }}
                onClick={() => setSelectedId(null)}
              >
                <p>All</p>
              </div>
              {FilterOptions.map((option) => (
                <div
                  key={option.id}
                  className={`${styles["contact-filters-buttons"]} ${
                    selectedId === option.id ? styles.filterButtonSelected : ""
                  }`}
                  onClick={() => setSelectedId(option.id)}
                >
                  <p>{option.label}</p>
                </div>
              ))}
            </div>
          </div>
            <div className={styles["msg"]}>
              {filteredData.length > 0 ? (
                filteredData
                  .filter((client) =>
                    client.name.toLowerCase().includes(searchText.toLowerCase())
                  )

                  .map((client, index) => {
                    return (
                      <>
                        <div
                          className={styles["message-div"]}
                          onClick={() =>
                            setClientSelected({
                              ...client, // Spread the original client object
                              id: (client.pastSession || client.upcomingSession).userID, // Explicitly set the ID for chat operations
                              name: client.name, // Ensure name is correctly set
                              clientIconUrl: client.avatar_url || avatar[client.gender] || avatar["other"], // Set the icon URL
                            })
                          }
                          style={{
                            borderBottomWidth: index === filteredData.length - 1 ? '0' : ''
                          }}
                        >
                          <img
                            src={client.clientIconUrl || avatar["other"]}
                            alt="user-avatar"
                            className={styles["clients-img"]}
                          />
                          <div className={styles["message-right"]}>
                            <div className={styles["msg-top1"]}>
                              <h3
                                className={styles.name}
                                style={{ textAlign: "center" }}
                              >
                                {client.name}
                              </h3>
                              <div
                                className={styles["contact-Chatbutton"]}
                                style={{
                                  
                                }}
                              >
                                <p>Chat Now</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        {index != clientsList.length - 1 && (
                          <div className={styles["divide-msg"]} style={{
                            borderWidth: index === filteredData.length - 1 ? '0' : ''
                          }}></div>
                        )}
                      </>
                    );
                  })
              ) : (
                <p className={styles["noClientsTxt"]}>No Clients available.</p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles["search-addIcon-header"]}>
              <div className={`${styles.search} ${styles.chatsl}`}>
                <FiSearch size={18} />
                <input
                  type="text"
                  className={styles["search-input"]}
                  placeholder="Search"
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                />
              </div>
              <div
                className={styles["plusIconbg"]}
                onClick={() => {setShowAllContacts(true); setClientSelected(null)}}
              >
                <p className={styles["plusIcon"]}>+</p>
              </div>
            </div>
            <div className={styles["msg"]}>
              {clientsList
                .filter((client) =>
                  client.name.toLowerCase().includes(searchText.toLowerCase())
                )
                .map((client, index) => {
                  return (
                    <>
                      <div
                        className={styles["message-div"]}
                        onClick={() => {
                          setClientSelected(client);
                        }}
                      >
                        <img
                          src={client.clientIconUrl || avatar["other"]}
                          alt="user-avatar"
                          className={styles["clients-img"]}
                        />
                        <div className={styles["message-right"]}>
                          <div className={styles["msg-top"]}>
                            <h3 className={styles.name}>{client.name}</h3>
                            <p className={styles.time}>
                              {moment(
                                client?.chats?.[
                                  client.chats.length - 1
                                ]?.timestamp?.toDate()
                              ).format("hh:mm A")}
                            </p>
                          </div>
                          <div className={styles["msg-bottom"]}>
                            <div className={styles["nmid-bottom"]}>
                              {client.chats &&
                                (client.type === "seen" ? (
                                  <IoCheckmarkDone
                                    size={15}
                                    color={"#49AF7C"}
                                  />
                                ) : (
                                  <IoCheckmarkDone size={15} />
                                ))}
                              <p>
                                {truncateText(
                                  client.chats?.[client.chats.length - 1]
                                    .textContent,
                                  38
                                )}
                              </p>
                            </div>
                            {client.count > 0 && (
                              <div className={styles["count-div"]}>
                                <p>{client.count}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {index != clientsList.length - 1 && (
                        <div className={styles["divide-msg"]}></div>
                      )}
                    </>
                  );
                })}
            </div>
          </>
        )}
      </div>
      <div
        className={`${styles["chats-right"]} ${
          !chatsShown ? styles.active : ""
        }`}
      >
        {!clientSelected ? (
          <div className={styles["cr-img"]}>
            <img
              src="https://ucarecdn.com/99bc4d72-8b4f-44d6-962e-30b72d0a0ae4/chatBliss.svg"
              alt="Lets's Chat"
              style={{ marginTop: "120px" }}
              // className="bliss-chat-img"
            />
          </div>
        ) : (
          <div className={styles["ichat-div"]}>
            <div className={styles["chats-back"]} onClick={handleChats}>
              <IoIosArrowBack size={20} />
            </div>
            <div className={styles["ichat-top"]}>
              <img
                src={clientSelected.clientIconUrl || avatar["other"]}
                alt="user-avatar"
              />
              <p>{clientSelected.name}</p>
            </div>
            <div className={styles["ichat-bottom"]}>
              <div className={styles["ichat-msgs"]}>
                {/* Calculate unread message count */}
                {clientSelected.count > 0 && !unreadMessageDisplayed && (
                  <div className={styles["unread"]}>
                    <div className={styles["unread-div"]}>
                      <p>
                        {clientSelected.count}{" "}
                        {clientSelected.count === 1
                          ? "Unread Message"
                          : "Unread Messages"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Map through the chats once */}
                {messageList.map((obj, index) => {
                  
                  return (
                    <div key={index}>
                      <p className={styles["chat-date"]}>{obj.date}</p>
                      <div>
                        {obj.senderId !== userInfo.idTherapist ? (
                          <div className={styles["receive-div"]}>
                            <div className={styles["receive-left"]}>
                              <img
                                src={obj.receiverIconUrl || avatar["other"]}
                                alt="user-avatar"
                              />
                              <p>{convertTimestampToTime(obj.timestamp || {seconds: new Date().getSeconds()})}</p>
                            </div>
                            <div className={styles["receive-right"]}>
                              <p>{obj.textContent}</p>
                            </div>
                          </div>
                        ) : (
                          <div className={styles["sender-div"]}>
                            <div className={styles["sender-left"]}>
                              {obj.textContent && <p>{obj.textContent}</p>}
                              {obj.file && (
                                <div>
                                  <a
                                    href={URL.createObjectURL(obj.file)}
                                    download={obj.file.name}
                                  >
                                    {obj.file.name}{" "}
                                    <MdOutlineDownloadForOffline size={20} />
                                  </a>
                                </div>
                              )}
                            </div>
                            {obj.status === 3 ? (
                              <IoCheckmarkDone size={20} color={"#49AF7C"} />
                            ) : (
                              <IoCheckmarkDone size={20} />
                            )}
                            <p className={styles["sender-time"]}>
                              {convertTimestampToTime(obj.timestamp || {seconds: new Date().getSeconds()} )}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={styles["ichat-send"]}>
                <div className={styles["send-input"]}>
                  <textarea
                    placeholder="Say something.."
                    value={message}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      height: `${inputHeight}px`,
                      minHeight: "30px",
                      maxHeight: "70px",
                    }}
                    rows={1}
                  />
                </div>
                {(message || file) && (
                  <button
                    className={styles["send-button"]}
                    onClick={handleMessageSend}
                  >
                    <VscSend color={"white"} size={22} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chats;
